/* eslint-disable no-else-return */
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { attachRequestLogger } from './request-logger';

export const BASE_URL = process.env.REACT_APP_BASE_URL;

attachRequestLogger(axios);

// Cache do user autenticado pra evitar refazer Auth.currentAuthenticatedUser()
// em CADA request. Esse metodo do Amplify pode disparar refresh de token
// (network call) e e chamado por todo interceptor — antes, abrir uma tela
// que dispara 4-5 requests em sequencia significava 4-5 round-trips de auth
// alem dos da API.
// TTL curto (30s) pra ainda pegar refresh de token relativamente rapido se
// o operador deslogar/relogar em outro lugar. Cognito faz refresh automatico
// internamente, entao o objeto e estavel dentro da janela.
let cachedUser = null;
let cachedAt = 0;
const AUTH_CACHE_TTL_MS = 30_000;

const currentUser = async () => {
  const now = Date.now();
  if (cachedUser && now - cachedAt < AUTH_CACHE_TTL_MS) {
    return cachedUser;
  }
  try {
    const user = await Auth.currentAuthenticatedUser();
    cachedUser = user;
    cachedAt = now;
    return user;
  } catch (error) {
    cachedUser = null;
    return false;
  }
};

const requestConfig = {
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
};

axios.interceptors.request.use(
  async (config) => {
    const userAuthenticated = await currentUser();

    if (userAuthenticated) {
      const {
        signInUserSession: { accessToken },
      } = userAuthenticated;
      // axios 1.x: config.headers e uma instancia AxiosHeaders — MUTAR a
      // propriedade preserva a instancia (recriar por spread virava objeto
      // plano e podia perder normalizacao de headers).
      config.headers.Authorization = config.url.includes('graphql')
        ? accessToken.jwtToken
        : `Bearer ${accessToken.jwtToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const fallback =
      (error && error.message) || 'Não foi possível completar a requisição';

    if (!error || !error.response) {
      return Promise.reject(fallback);
    }

    const { data, status } = error.response;
    const formatted =
      (data && (data.message || data.error || data)) ||
      `Erro HTTP ${status || ''}`.trim();

    return Promise.reject(formatted);
  }
);

const request = (url, method, body, customConfig, params, signal) => {
  try {
    if (method === 'report') {
      return axios.get(url, {
        responseType: 'blob', // important
        signal,
      });
    } else if (method === 'new-report') {
      return axios.post(url, body, {
        responseType: 'blob', // important
        signal,
      });
    } else {
      const config = customConfig || requestConfig;
      return axios({
        method,
        url,
        data: body || [],
        params,
        signal,
        ...config,
      });
    }
  } catch (error) {
    return error;
  }
};

// Hook utility: cria um AbortController atrelado ao ciclo de vida do componente.
// Use:
//   const getSignal = useAbortSignal();
//   getDataDashboard(id, type, getSignal())  // se a API suportar signal
//   // ou passe getSignal() para qualquer função que aceite signal
// Ao desmontar, o controller é abortado automaticamente, cancelando requests
// em-voo (axios rejeita com erro "canceled" que pode ser detectado por
// axios.isCancel(err) ou err.code === 'ERR_CANCELED').
export const isAbortError = (error) =>
  axios.isCancel(error) ||
  (error && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError'));

export default request;
